import { useMemo } from 'react'
import { useTexture } from '@react-three/drei'
import { SRGBColorSpace, Texture } from 'three'
import { textureAsset } from '../../assetStore'

export type TopIcon = 'heart' | 'coin' | 'shield'
export type BottomIcon = 'battery' | 'coin' | 'shield' | 'star'

export type EndCardVariantAssetsConfig = {
    nameTexture: string
    top: {
        left: { icon: TopIcon }
        right: { icon: TopIcon }
    }
    bottom: {
        left: { icon: BottomIcon }
        center: { icon: BottomIcon }
        right: { icon: BottomIcon }
    }
}

const collectableTextureByIcon: Record<TopIcon, string> = {
    heart: textureAsset.EndCardCollectableHeart,
    coin: textureAsset.EndCardCollectableCoin,
    shield: textureAsset.EndCardCollectableShield,
}

const powerUpTextureByIcon: Record<BottomIcon, string> = {
    battery: textureAsset.EndCardPowerUpBattery,
    coin: textureAsset.EndCardPowerUpCoin,
    shield: textureAsset.EndCardPowerUpShield,
    star: textureAsset.EndCardPowerUpStar,
}

const allCardTextureUrls = [
    textureAsset.EndCardNameStarChaser,
    textureAsset.EndCardNameRushGuard,
    textureAsset.EndCardNamePowerCharger,
    textureAsset.EndCardNameTreasureHunter,
    textureAsset.EndCardCollectableHeart,
    textureAsset.EndCardCollectableCoin,
    textureAsset.EndCardCollectableShield,
    textureAsset.EndCardPowerUpBattery,
    textureAsset.EndCardPowerUpCoin,
    textureAsset.EndCardPowerUpShield,
    textureAsset.EndCardPowerUpStar,
] as const

function configureTexture(texture: Texture) {
    texture.flipY = false
    texture.colorSpace = SRGBColorSpace
}

export function useEndCardVariantAssets(variant: EndCardVariantAssetsConfig) {
    const textures = useTexture([...allCardTextureUrls])
    textures.forEach(configureTexture)

    const textureMap = useMemo(() => {
        return allCardTextureUrls.reduce<Record<string, Texture>>((acc, url, index) => {
            acc[url] = textures[index]
            return acc
        }, {})
    }, [textures])

    return {
        nameTexture: textureMap[variant.nameTexture],
        collectableLeftTexture: textureMap[collectableTextureByIcon[variant.top.left.icon]],
        collectableRightTexture: textureMap[collectableTextureByIcon[variant.top.right.icon]],
        powerUpLeftTexture: textureMap[powerUpTextureByIcon[variant.bottom.left.icon]],
        powerUpCenterTexture: textureMap[powerUpTextureByIcon[variant.bottom.center.icon]],
        powerUpRightTexture: textureMap[powerUpTextureByIcon[variant.bottom.right.icon]],
    }
}
